import React, { useState, useContext } from 'react';
import {
	TextField,
	Container,
	Stack,
	Button,
	Paper,
	Grid,
	InputAdornment,
	Box,
	IconButton,
	Typography
} from '@mui/material';
import { Close, ZoomIn } from '@mui/icons-material';

import { DataContext } from '../../../../context/DataContext';
import { simpleMessage } from '../../../../helpers/Helpers';
import { getToken } from '../../../../api/Account';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import { updateGarantiaAsync } from '../../../../api/Credits';
import PreviewModal from '../../../../components/modals/PreviewModal';

const EditGarantia = ({ setShowModal, localReload, setLocalReload, selectedGarantia }) => {
	const { id } = selectedGarantia;

	// 📝 Estados del formulario
	const [articulo, setArticulo] = useState(selectedGarantia.articulo || '');
	const [marca, setMarca] = useState(selectedGarantia.marca || '');
	const [color, setColor] = useState(selectedGarantia.color || '');
	const [modelo, setModelo] = useState(selectedGarantia.modelo || '');
	const [serie, setSerie] = useState(selectedGarantia.serie || '');
	const [valorEstimado, setValorEstimado] = useState(selectedGarantia.valorEstimado || '');

	// 📸 Fotos
	const [garantiaPhotos] = useState(selectedGarantia.garantiaPhotos || []);
	const [previews, setPreviews] = useState([]);
	const [selectedFiles, setSelectedFiles] = useState([]);

	// 🔍 Modal de preview
	const [openModal, setOpenModal] = useState(false);
	const [selectedImage, setSelectedImage] = useState('');

	const { setIsLoading, setIsUnautorized } = useContext(DataContext);

	const token = getToken();

	const funtionSetValorEstimado = (value) => {
		if (/^[0-9]+$/.test(value.toString()) || value === '') {
			setValorEstimado(value);
			return;
		}
	};

	const saveChanges = async () => {
		if (validate()) {
			const formData = new FormData();

			formData.append('id', id);
			formData.append('articulo', articulo);
			formData.append('marca', marca);
			formData.append('color', color);
			formData.append('modelo', modelo);
			formData.append('serie', serie);
			formData.append('valorEstimado', valorEstimado);

			selectedFiles.forEach((photo, index) => {
				formData.append(`garantiaPhoto`, photo); // o photo si es directamente el File
			});

			if (marca === '') {
				formData.append('marca', 'S/M');
			}

			if (modelo === '') {
				formData.append('modelo', 'S/M');
			}

			if (serie === '') {
				formData.append('serie', 'S/S');
			}
			setIsLoading(true);

			const result = await updateGarantiaAsync(token, formData);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setIsLoading(false);
			simpleMessage('Garantia Actualizada...!', 'success');
			setLocalReload(!localReload);
			setShowModal(false);
		}
	};

	const validate = () => {
		let isValid = true;
		if (articulo === '') {
			simpleMessage('Debe ingresar un artículo', 'error');
			return (isValid = false);
		}

		if (color === '') {
			simpleMessage('Debe ingresar un color', 'error');
			return (isValid = false);
		}

		if (valorEstimado === '' || valorEstimado === 0) {
			simpleMessage('Debe ingresar un valor al artículo', 'error');
			return (isValid = false);
		}

		if (selectedFiles.length < 1 && garantiaPhotos.length < 1) {
			simpleMessage('Debe seleccionar al menos una foto', 'error');
			return (isValid = false);
		}
		return isValid;
	};

	const removeImage = (index) => {
		// Revocar la URL para liberar memoria
		URL.revokeObjectURL(previews[index].preview);

		const newPreviews = previews.filter((_, i) => i !== index);
		const newFiles = selectedFiles.filter((_, i) => i !== index);

		setPreviews(newPreviews);
		setSelectedFiles(newFiles);
	};

	const onChangeFile = (e) => {
		const files = Array.from(e.target.files);

		if (!files) return;

		if (files.length > 1) {
			simpleMessage('Solo puedes seleccionar máximo 1 archivo', 'error');
			e.target.value = ''; // Limpiar la selección
			return;
		}

		// Crear vistas previas
		const newPreviews = files.map((file) => ({
			file,
			preview: URL.createObjectURL(file)
		}));

		setSelectedFiles(files);
		setPreviews(newPreviews);
	};

	// Limpiar URLs cuando el componente se desmonte
	React.useEffect(() => {
		return () => {
			previews.forEach((preview) => URL.revokeObjectURL(preview.preview));
		};
	}, []);

	const handleOpen = (imageUrl) => {
		setSelectedImage(imageUrl);
		setOpenModal(true);
	};

	return (
		<div>
			<Container style={{ width: 550 }}>
				<Paper
					style={{
						padding: 20
					}}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<Stack spacing={2}>
								<TextField
									required
									fullWidth
									onChange={(e) => setArticulo(e.target.value.toUpperCase())}
									value={articulo}
									label={'Articulo'}
								/>
								<TextField
									fullWidth
									onChange={(e) => setMarca(e.target.value.toUpperCase())}
									value={marca}
									label={'Marca'}
								/>
								<TextField
									required
									fullWidth
									onChange={(e) => setColor(e.target.value.toUpperCase())}
									value={color}
									label={'Color'}
								/>
							</Stack>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Stack spacing={2}>
								<TextField
									fullWidth
									onChange={(e) => setModelo(e.target.value.toUpperCase())}
									value={modelo}
									label={'Modelo'}
								/>
								<TextField
									fullWidth
									onChange={(e) => setSerie(e.target.value.toUpperCase())}
									value={serie}
									label={'Serie'}
								/>
								<TextField
									required
									fullWidth
									onChange={(e) => funtionSetValorEstimado(e.target.value)}
									value={valorEstimado}
									label={'Valor estimado'}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">C$</InputAdornment>
										)
									}}
								/>
							</Stack>
						</Grid>

						{/* 📸 Imágenes */}
						<Grid item xs={12}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Fotos del Cliente
							</Typography>
							<TextField
								fullWidth
								type="file"
								inputProps={{ accept: 'image/*' }}
								onChange={onChangeFile}
							/>

							<Box sx={{ mt: 2 }}>
								{(previews.length > 0 ? previews : garantiaPhotos).map(
									(preview, index) => {
										const imageUrl = preview.preview || preview.imageFullPath;

										return (
											<Paper
												key={index}
												sx={{
													position: 'relative',
													p: 1,
													height: 220,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													mb: 2
												}}
											>
												<img
													src={imageUrl}
													alt={`Vista previa ${index + 1}`}
													style={{
														maxWidth: '100%',
														maxHeight: '100%',
														objectFit: 'contain'
													}}
												/>

												{/* 🔍 Zoom */}
												<IconButton
													sx={{
														position: 'absolute',
														top: 8,
														right: 8,
														backgroundColor: 'rgba(255, 255, 255, 0.1)',
														'&:hover': {
															backgroundColor:
																'rgba(255, 255, 255, 0.5)'
														},
														width: 32,
														height: 32
													}}
													onClick={() => handleOpen(imageUrl)}
												>
													<ZoomIn fontSize="small" />
												</IconButton>

												{/* ❌ Eliminar (solo nuevas) */}
												{/* {previews.length > 0 && (
													<IconButton
														sx={{ ...btnOverlayStyle, right: 48 }}
														onClick={() => removeImage(index)}
													>
														<Delete fontSize="small" />
													</IconButton>
												)} */}
											</Paper>
										);
									}
								)}
							</Box>
						</Grid>
						<Grid item xs={12}>
							<Button
								variant="outlined"
								startIcon={
									<FontAwesomeIcon icon={faSave} style={{ marginRight: 20 }} />
								}
								fullWidth
								onClick={() => saveChanges()}
								style={{
									color: '#00a152',
									borderColor: '#00a152'
								}}
								size="large"
							>
								Atualizar garantia
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Container>

			<PreviewModal
				openModal={openModal}
				selectedImage={selectedImage}
				setOpenModal={setOpenModal}
				setSelectedImage={setSelectedImage}
			/>
		</div>
	);
};

export default EditGarantia;
